import { cn } from "./classNames-DcGnTh2N.js";
import { Tooltip } from "./utils-BLJzxVW-.js";
import { Maximize2, Minimize2 } from "./icons-ZIeZXXS7.js";
import { Button } from "./Button-D2JqBAq7.js";
import { ClipboardButton } from "./ClipboardButton-4dvZHyq_.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { createPortal } from "react-dom";
import DOMPurify from "dompurify";
//#region src/components/ZoomableSvg/ZoomableSvg.tsx
const MIN_SCALE = .5;
const MAX_SCALE = 3;
const ZOOM_STEP = .1;
const ZoomableSvg = ({ html, copyText, className, allowForeignObject, allowUse, renderActions }) => {
	const safeHtml = useMemo(() => DOMPurify.sanitize(html, {
		USE_PROFILES: {
			svg: true,
			svgFilters: true
		},
		ADD_TAGS: [...allowForeignObject ? ["foreignObject"] : [], ...allowUse ? ["use"] : []]
	}), [
		html,
		allowForeignObject,
		allowUse
	]);
	const containerRef = useRef(null);
	const [scale, setScale] = useState(1);
	const [translate, setTranslate] = useState({
		x: 0,
		y: 0
	});
	const [isFullscreen, setIsFullscreen] = useState(false);
	useEffect(() => {
		if (!isFullscreen) return;
		const onKeyDown = (e) => {
			if (e.key === "Escape") {
				e.stopPropagation();
				setIsFullscreen(false);
			}
		};
		document.addEventListener("keydown", onKeyDown, { capture: true });
		return () => document.removeEventListener("keydown", onKeyDown, { capture: true });
	}, [isFullscreen]);
	const dragRef = useRef(null);
	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const onWheel = (e) => {
			if (!e.ctrlKey && !e.metaKey) return;
			e.preventDefault();
			const delta = e.deltaY > 0 ? -.1 : ZOOM_STEP;
			setScale((s) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s + delta)));
		};
		el.addEventListener("wheel", onWheel, { passive: false });
		return () => el.removeEventListener("wheel", onWheel);
	}, [isFullscreen]);
	const didDragRef = useRef(false);
	const handleMouseDown = useCallback((e) => {
		if (e.button !== 0) return;
		e.preventDefault();
		didDragRef.current = false;
		dragRef.current = {
			startX: e.clientX,
			startY: e.clientY,
			startTx: translate.x,
			startTy: translate.y
		};
	}, [translate]);
	const handleMouseMove = useCallback((e) => {
		if (!dragRef.current) return;
		didDragRef.current = true;
		setTranslate({
			x: dragRef.current.startTx + e.clientX - dragRef.current.startX,
			y: dragRef.current.startTy + e.clientY - dragRef.current.startY
		});
	}, []);
	const handleMouseUp = useCallback(() => {
		dragRef.current = null;
	}, []);
	const handleReset = useCallback(() => {
		setScale(1);
		setTranslate({
			x: 0,
			y: 0
		});
	}, []);
	const isZoomed = Math.abs(scale - 1) > .001 || translate.x !== 0 || translate.y !== 0;
	const handleClick = useCallback((e) => {
		if (didDragRef.current) e.stopPropagation();
	}, []);
	const stopPropagation = useCallback((e) => {
		e.stopPropagation();
	}, []);
	const diagramContent = /* @__PURE__ */ jsxs("div", {
		role: "presentation",
		className: cn("tw:group/zoom tw:relative", isFullscreen && "tw:h-full"),
		onClick: handleClick,
		children: [/* @__PURE__ */ jsx("div", {
			role: "presentation",
			ref: containerRef,
			className: cn("tw:overflow-hidden tw:cursor-grab tw:active:cursor-grabbing tw:p-3", isFullscreen && "tw:h-full"),
			onMouseDown: handleMouseDown,
			onMouseMove: handleMouseMove,
			onMouseUp: handleMouseUp,
			onMouseLeave: handleMouseUp,
			children: /* @__PURE__ */ jsx("div", {
				className: cn("tw:flex tw:justify-center tw:origin-center", isFullscreen && "tw:h-full tw:[&>svg]:h-full"),
				style: { transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})` },
				dangerouslySetInnerHTML: { __html: safeHtml }
			})
		}), /* @__PURE__ */ jsxs("div", {
			role: "presentation",
			className: "tw:absolute tw:right-1 tw:top-1 tw:flex tw:gap-1 tw:opacity-0 tw:transition-opacity tw:focus-within:opacity-100 tw:group-hover/zoom:opacity-100",
			onClick: stopPropagation,
			children: [
				isZoomed && /* @__PURE__ */ jsx(Tooltip, {
					tooltip: "Reset zoom",
					placement: "top",
					triggerAsChild: true,
					children: /* @__PURE__ */ jsx(Button, {
						size: "small",
						variant: "outline",
						onClick: handleReset,
						children: "Reset"
					})
				}),
				copyText && /* @__PURE__ */ jsx(ClipboardButton, {
					text: copyText,
					tooltip: "Copy code",
					size: "icon-sm",
					variant: "outline"
				}),
				renderActions?.(safeHtml),
				/* @__PURE__ */ jsx(Tooltip, {
					tooltip: isFullscreen ? "Exit fullscreen" : "Fullscreen",
					placement: "top",
					triggerAsChild: true,
					children: /* @__PURE__ */ jsx(Button, {
						size: "icon-sm",
						variant: "outline",
						"aria-label": isFullscreen ? "Exit fullscreen" : "Fullscreen",
						onClick: () => setIsFullscreen((f) => !f),
						children: isFullscreen ? /* @__PURE__ */ jsx(Minimize2, { className: "tw:size-4" }) : /* @__PURE__ */ jsx(Maximize2, { className: "tw:size-4" })
					})
				})
			]
		})]
	});
	if (isFullscreen) return createPortal(/* @__PURE__ */ jsx("div", {
		role: "presentation",
		"data-testid": "zoomable-svg-fullscreen",
		className: "tw:fixed tw:inset-0 tw:z-[1051] tw:bg-muted tw:p-4 tailwindcss-preflight",
		onClick: stopPropagation,
		children: diagramContent
	}), document.body);
	return /* @__PURE__ */ jsx("div", {
		className: cn("tw:overflow-hidden tw:rounded-md tw:border tw:border-border tw:bg-muted", className),
		children: diagramContent
	});
};
//#endregion
//#region src/components/ZoomableSvg/exportSvg.ts
const PNG_SCALE = 2;
const ROOT_SVG_TAG = /^<svg\b[^>]*>/i;
const HAS_DEFAULT_NAMESPACE = /\bxmlns\s*=/i;
const withSvgNamespace = (svg) => {
	const trimmed = svg.trim();
	const rootTag = trimmed.match(ROOT_SVG_TAG)?.[0];
	if (!rootTag || HAS_DEFAULT_NAMESPACE.test(rootTag)) return trimmed;
	return trimmed.replace(/^<svg\b/i, "<svg xmlns=\"http://www.w3.org/2000/svg\"");
};
const toSvgFileContent = (svg) => `<?xml version="1.0" encoding="UTF-8"?>\n${withSvgNamespace(svg).trim()}\n`;
const NUMBER = String.raw`\d+(?:\.\d+)?|\.\d+`;
const PIXEL_LENGTH = new RegExp(String.raw`^\s*(?:${NUMBER})(?:px)?\s*$`, "i");
const parsePixelLength = (value) => {
	if (!PIXEL_LENGTH.test(value ?? "")) return null;
	const parsed = Number.parseFloat(value ?? "");
	return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};
const parseSvgSize = (svg) => {
	const element = new DOMParser().parseFromString(withSvgNamespace(svg), "image/svg+xml").documentElement;
	const viewBox = element.getAttribute("viewBox")?.split(/[\s,]+/).map(Number);
	if (viewBox?.length === 4 && viewBox.every((value) => Number.isFinite(value))) {
		const [, , width, height] = viewBox;
		if (width !== void 0 && height !== void 0 && width > 0 && height > 0) return {
			width,
			height
		};
	}
	const width = parsePixelLength(element.getAttribute("width"));
	const height = parsePixelLength(element.getAttribute("height"));
	return width !== null && height !== null ? {
		width,
		height
	} : null;
};
const CHANNEL_TRIPLET = new RegExp(String.raw`^(?:${NUMBER})\s+(?:${NUMBER})\s+(?:${NUMBER})$`);
const toCanvasColor = (token) => {
	const trimmed = token.trim();
	if (!trimmed) return "white";
	return CHANNEL_TRIPLET.test(trimmed) ? `rgb(${trimmed})` : trimmed;
};
const resolveBackground = () => toCanvasColor(getComputedStyle(document.documentElement).getPropertyValue("--color-background"));
/**
* Rasterize SVG markup to a PNG blob at 2x for legible text.
*
* The SVG is loaded as a data URL rather than a blob URL: an <img> loading SVG
* is treated as a foreign resource, so a blob URL would taint the canvas and
* make toBlob throw a SecurityError. A background is painted first because a
* transparent PNG reads as unusable when pasted into a slide deck.
*/
const svgToPngBlob = async (svg, background) => {
	const size = parseSvgSize(svg);
	if (!size) throw new Error("Cannot determine diagram dimensions.");
	const encoded = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(toSvgFileContent(svg))}`;
	const image = new Image();
	image.decoding = "sync";
	await new Promise((resolve, reject) => {
		image.onload = () => resolve();
		image.onerror = () => reject(/* @__PURE__ */ new Error("Failed to load the diagram for export."));
		image.src = encoded;
	});
	const canvas = document.createElement("canvas");
	canvas.width = Math.round(size.width * PNG_SCALE);
	canvas.height = Math.round(size.height * PNG_SCALE);
	const context = canvas.getContext("2d");
	if (!context) throw new Error("Canvas is unavailable in this browser.");
	context.fillStyle = background ?? resolveBackground();
	context.fillRect(0, 0, canvas.width, canvas.height);
	context.drawImage(image, 0, 0, canvas.width, canvas.height);
	return new Promise((resolve, reject) => {
		canvas.toBlob((blob) => blob ? resolve(blob) : reject(/* @__PURE__ */ new Error("Failed to encode the PNG.")), "image/png");
	});
};
//#endregion
export { ZoomableSvg, svgToPngBlob, toSvgFileContent };

//# sourceMappingURL=ZoomableSvg-3bE8YRQa.js.map